// Calculate support and resistance from candle data
export function calculateSupportResistance(candles) {
  if (!candles || candles.length < 5) {
    return { support: null, resistance: null };
  }
  
  const recent = candles.slice(-20);
  const lows = recent.map(c => c.l);
  const highs = recent.map(c => c.h);
  
  return {
    support: +Math.min(...lows).toFixed(2),
    resistance: +Math.max(...highs).toFixed(2)
  };
}

// Parse Finnhub candle response to array format
export function parseCandles(data) {
  if (!data || data.s !== "ok" || !data.t) {
    return null;
  }
  
  return data.t.map((_, i) => ({
    t: data.t[i],
    o: data.o[i],
    h: data.h[i],
    l: data.l[i],
    c: data.c[i],
    v: data.v[i]
  }));
}

// Get trading signal based on position and price
export function getSignal(position, currentPrice, supportResistance) {
  if (!currentPrice) {
    return { label: "—", bgColor: "bg-muted", textColor: "text-muted-foreground" };
  }
  
  const pnlPercent = ((currentPrice - position.avg) / position.avg) * 100;
  const sl = position.stop_loss;

  // STOP LOSS — highest priority if user defined one
  if (sl && currentPrice <= sl) {
    return { label: "STOP LOSS", bgColor: "bg-destructive/20", textColor: "text-destructive" };
  }
  if (sl && currentPrice <= sl * 1.03) {
    return { label: "PROCHE STOP", bgColor: "bg-warning/20", textColor: "text-warning" };
  }

  const nearResistance = supportResistance?.resistance && 
    Math.abs(currentPrice - supportResistance.resistance) / supportResistance.resistance < 0.02;
  const nearSupport = supportResistance?.support && 
    Math.abs(currentPrice - supportResistance.support) / supportResistance.support < 0.02;
  const isSwing = (position.strategy || "LT") === "ST";
  
  if (isSwing) {
    // SWING TRADING signals — tighter thresholds, faster exits
    if (pnlPercent > 20) return { label: "PRENDRE PROFIT", bgColor: "bg-warning/20", textColor: "text-warning" };
    if (nearResistance && pnlPercent > 2) return { label: "VENDRE", bgColor: "bg-destructive/20", textColor: "text-destructive" };
    if (pnlPercent < -10) return { label: "STOP LOSS", bgColor: "bg-destructive/20", textColor: "text-destructive" };
    if (nearSupport && pnlPercent < -2) return { label: "RENFORCER", bgColor: "bg-accent/20", textColor: "text-accent" };
    if (pnlPercent < -5) return { label: "SURVEILLER", bgColor: "bg-warning/20", textColor: "text-warning" };
    if (pnlPercent > 5) return { label: "LAISSER COURIR", bgColor: "bg-accent/20", textColor: "text-accent" };
    return { label: "NEUTRE", bgColor: "bg-primary/20", textColor: "text-primary" };
  }
  
  // LONG TERM signals — wider thresholds, patience
  if (nearResistance && pnlPercent > 3) return { label: "VENDRE", bgColor: "bg-destructive/20", textColor: "text-destructive" };
  if (nearSupport && pnlPercent < -3) return { label: "RENFORCER", bgColor: "bg-accent/20", textColor: "text-accent" };
  if (pnlPercent > 100) return { label: "VENDRE 50%", bgColor: "bg-warning/20", textColor: "text-warning" };
  if (pnlPercent < -70) return { label: "VENDRE", bgColor: "bg-destructive/20", textColor: "text-destructive" };
  if (pnlPercent < -45) return { label: "VENDRE", bgColor: "bg-destructive/20", textColor: "text-destructive" };
  if (position.sector?.includes("Défense") || position.sector === "Or" || position.sector?.includes("ETF")) {
    return { label: "CONSERVER", bgColor: "bg-accent/20", textColor: "text-accent" };
  }
  if (pnlPercent < -20) return { label: "SURVEILLER", bgColor: "bg-warning/20", textColor: "text-warning" };
  return { label: "CONSERVER", bgColor: "bg-primary/20", textColor: "text-primary" };
}

// Format number with locale
export function formatNumber(num, decimals = 2) {
  if (num === null || num === undefined) return "—";
  return num.toLocaleString('fr-FR', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
}

// Format currency
export function formatCurrency(num, currency = "€") {
  if (num === null || num === undefined) return "—";
  return `${Math.round(num).toLocaleString('fr-FR')}${currency}`;
}

// Get currency symbol from ticker
export function getTickerCurrency(ticker) {
  if (!ticker) return "$";
  if (ticker.endsWith(".PA") || ticker.endsWith(".DE") || ticker.endsWith(".MI") || 
      ticker.endsWith(".AS") || ticker.endsWith(".HE") || ticker.endsWith(".MC") ||
      ticker.endsWith(".LS") || ticker.endsWith(".BR")) return "€";
  if (ticker.endsWith(".L")) return "£";
  if (ticker.endsWith(".T")) return "¥";
  if (ticker.endsWith(".KS")) return "₩";
  if (ticker.endsWith(".HK")) return "HK$";
  if (ticker.endsWith(".NS")) return "₹";
  if (ticker.endsWith(".SW")) return "CHF ";
  if (ticker.endsWith(".ST")) return "kr ";
  if (ticker.endsWith(".CO")) return "kr ";
  if (ticker.endsWith(".OL")) return "kr ";
  if (ticker.endsWith(".AX")) return "A$";
  if (ticker.includes("-USD")) return "$";
  if (ticker.includes("=F")) return "$";
  return "$";
}

// Get ISO currency code from ticker (for conversion)
export function getTickerCurrencyCode(ticker) {
  if (!ticker) return "USD";
  if (ticker.endsWith(".PA") || ticker.endsWith(".DE") || ticker.endsWith(".MI") || 
      ticker.endsWith(".AS") || ticker.endsWith(".HE") || ticker.endsWith(".MC") ||
      ticker.endsWith(".LS") || ticker.endsWith(".BR")) return "EUR";
  if (ticker.endsWith(".L")) return "GBP";
  if (ticker.endsWith(".T")) return "JPY";
  if (ticker.endsWith(".KS")) return "KRW";
  if (ticker.endsWith(".HK")) return "HKD";
  if (ticker.endsWith(".NS")) return "INR";
  if (ticker.endsWith(".SW")) return "CHF";
  if (ticker.endsWith(".ST")) return "SEK";
  if (ticker.endsWith(".CO")) return "DKK";
  if (ticker.endsWith(".OL")) return "NOK";
  if (ticker.endsWith(".AX")) return "AUD";
  return "USD";
}

// Format price with correct currency for ticker
export function formatTickerPrice(price, ticker) {
  if (price === null || price === undefined || price === 0) return "—";
  const curr = getTickerCurrency(ticker);
  // For large prices (JPY, KRW, GBX pence), no decimals
  if (curr === "¥" || curr === "₩") {
    return `${curr}${Math.round(price).toLocaleString()}`;
  }
  // For HK$, kr, CHF - 2 decimals
  if (curr === "HK$" || curr.includes("kr") || curr.includes("CHF") || curr === "A$") {
    return `${curr}${price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  }
  // Standard currencies (€, £, $, ₹)
  return `${curr}${price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
}

// Format percentage with sign
export function formatPercent(num) {
  if (num === null || num === undefined) return "—";
  const sign = num >= 0 ? "+" : "";
  return `${sign}${num.toFixed(2)}%`;
}

// Get scan signal based on price action
export function getScanSignal(change, nearSupport, nearResistance) {
  if (change > 4) {
    return { label: "Fort momentum", color: "text-accent", bg: "bg-accent/20" };
  }
  if (change < -4) {
    return { label: "Survente", color: "text-warning", bg: "bg-warning/20" };
  }
  if (nearSupport) {
    return { label: "Sur support", color: "text-primary", bg: "bg-primary/20" };
  }
  if (nearResistance) {
    return { label: "Résistance", color: "text-destructive", bg: "bg-destructive/20" };
  }
  return { label: "Neutre", color: "text-muted-foreground", bg: "bg-muted" };
}

// Delay helper
export const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
